import { ChangeDetectorRef, Component, forwardRef, ElementRef } from '@angular/core';
import { ICheckBox } from '@shared/interfaces/form-controls';
import { IRegulatedRegCompReportCriteria } from '@reporting/interfaces/regulated-regcomp-report';
import { UserAccess } from '@core/authorization/user-access';
import { Globals } from '@shared/models/globals';
import { GridOptions, RowNode, SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import { DialogBase, M27Dialog, DataGrid, DialogButton, Dialogs, IColDefPlus } from '@m27/common';
import { PermitService } from '@shared/services';
import { ReportService } from '@reporting/services';
import { Subscription, timer } from 'rxjs';
import { IGeographyDropDown, IPermitSearch, IPermit } from '@shared/interfaces';
import { DataGridColumns } from '@shared/models';

@Component({
    selector: 'reporting-dialog-regulated-regcomp-report-criteria',
    templateUrl: './dialog-regulated-regcomp-report-criteria.component.html',
    styleUrls: ['./dialog-regulated-regcomp-report-criteria.component.scss'],
    providers: [{ provide: DialogBase, useExisting: forwardRef(() => RegulatedRegCompReportCriteriaDialogComponent) }]
})
export class RegulatedRegCompReportCriteriaDialogComponent extends DialogBase implements M27Dialog {
    public gridOptions: GridOptions;
    public totalRowCount = 0;
    public userAccess: UserAccess = Globals.UAC;
    public selectedRowCount = 0;
    public hasSearched = false;
    public isSearching = false;

    public availableCrops: ICheckBox[] = [
        { Name: 'Corn', Value: 'CRN', IsChecked: false, IsDisabled: false },
        { Name: 'Soybeans', Value: 'SOY', IsChecked: false, IsDisabled: false },
        { Name: 'Sorghum', Value: 'SORG', IsChecked: false, IsDisabled: false },
        { Name: 'Canola', Value: 'CANO', IsChecked: false, IsDisabled: false },
        { Name: 'Wheat', Value: 'WHE', IsChecked: false, IsDisabled: false },
        { Name: 'Cotton', Value: 'COT', IsChecked: false, IsDisabled: false },
        { Name: 'Alfalfa', Value: 'ALF', IsChecked: false, IsDisabled: false }
    ];
    public reportItems = [
        { Name: 'Information Dates', Value: 1, IsChecked: false, IsDisabled: false },
        { Name: 'Plant - Harvest', Value: 2, IsChecked: false, IsDisabled: false },
        { Name: 'Field Monitoring', Value: 3, IsChecked: false, IsDisabled: false },
        { Name: 'Final Disposition', Value: 4, IsChecked: false, IsDisabled: false },
        { Name: 'Post Trial', Value: 5, IsChecked: false, IsDisabled: false  }
    ];
    private numberOfYears = 10;
    public availableYears: ICheckBox[] = [];
    public selectedCountryList: IGeographyDropDown[] =  [
        { id: 539230, itemName: 'US w/ PR' }
    ];
    public selectedCountyList: IGeographyDropDown[] = [];
    public selectedStateList: IGeographyDropDown[] = [];
    public selectedReport: number;
    public experimentName = '';
    public locationName = '';
    public reportIdJustCreated = 0;
    public showReportJustCreatedBadge = false;
    private showNewReportBadgeSubscription: Subscription;

    // this is only calls once for entire web session
    constructor(private permitService: PermitService, private reportService: ReportService, cd: ChangeDetectorRef, elRef: ElementRef) {
        super(cd, elRef);

        const startYear: number = (new Date()).getFullYear();

        for (let x = 0; x < this.numberOfYears; x++) {
            this.availableYears.push({ Name: `${startYear - x}`, Value: startYear - x, IsChecked: false, IsDisabled: false });
        }
    }

    // this is only calls once for entire web session
    public onDialogReady(): void {
        this.setupDataGrid();
    }

    onCheckboxChange(event: any): void {
        setTimeout(() => {
            this.reportItems.forEach(p => p.IsChecked = false);
            this.reportItems.filter(p => 'chkRR' + p.Name === event.target.id).forEach(p => p.IsChecked = true);
        });
    }

    private setupDataGrid(): void {
        this.gridOptions = DataGrid.GetDefaults(this, 'REGULATED-REGCOMP-REPORT-PERMITS', () => { }, 'PermitId');

        this.gridOptions.columnDefs = [
            DataGrid.ColumnBuilder()
                .ForRowCheckBox()
                .Build(),

            ...DataGridColumns.PermitSearchResults()
        ];

        this.gridOptions.onSelectionChanged = (event: SelectionChangedEvent) => {
            this.selectedRowCount = this.gridOptions.api.getSelectedRows().filter((row: RowNode) => {
                return !row.group;
            }).length;
        };
    }

    private setupButtons(): void {
        if (this.isBackButtonEnabled()) {
            this.leftButtons.push({
                IsCancel: true,
                Text: 'Back',
                Action: (dialogBase: DialogBase) => {
                    this.close();
                }
            } as DialogButton);
        }

        this.rightButtons.push({
            IsCancel: false,
            Text: 'Run Report (Stay)',
            IsDisabled: () => {
                return !this.validateCanRunReport();
            },
            Action: (dialogBase: DialogBase) => {
                this.runReport(true);
            }
        } as DialogButton);

        this.rightButtons.push({
            IsCancel: false,
            Text: 'Run Report (Go)',
            IsDisabled: () => {
                return !this.validateCanRunReport();
            },
            Action: (dialogBase: DialogBase) => {
                this.runReport(false);
            }
        } as DialogButton);
    }

    private runReport(stay: boolean): void {
        if (this.reportItems.filter(item => item.IsChecked)[0].Value === 1) {
            this.reportService.RunRegulatedRegCompReport({
                PermitIds: this.gridOptions.api.getSelectedNodes().map(row => row.data.PermitId),
                CountyIds: this.selectedCountyList.map(row => row.id),
                StateIds: this.selectedStateList.map(row => row.id),
                ExperimentName: this.experimentName,
                LocationName: this.locationName,
                JobType: this.reportItems.filter(item => item.IsChecked)[0].Value
            } as IRegulatedRegCompReportCriteria).subscribe((reportId: number) => {
                if (!stay) {
                    Dialogs.navigate(`/reporting/reports/regulatedregcomp-infodates/${reportId}`);
                    return;
                }

                this.reportIdJustCreated = reportId;
                this.showReportJustCreatedBadge = true;

                if (this.showNewReportBadgeSubscription !== undefined) {
                    this.showNewReportBadgeSubscription.unsubscribe();
                }

                this.showNewReportBadgeSubscription = timer(3500).subscribe(() => {
                    this.showReportJustCreatedBadge = false;
                });
            });
        }
        else {
            this.reportService.RunRegulatedRegCompReport({
                PermitIds: this.gridOptions.api.getSelectedNodes().map(row => row.data.PermitId),
                CountyIds: this.selectedCountyList.map(row => row.id),
                StateIds: this.selectedStateList.map(row => row.id),
                ExperimentName: this.experimentName,
                LocationName: this.locationName,
                JobType: this.reportItems.filter(item => item.IsChecked)[0].Value
            } as IRegulatedRegCompReportCriteria).subscribe((reportId: number) => {
                if (!stay) {
                    Dialogs.navigate(`/reporting/reports/regulatedregcomp/${reportId}`);
                    return;
                }

                this.reportIdJustCreated = reportId;
                this.showReportJustCreatedBadge = true;

                if (this.showNewReportBadgeSubscription !== undefined) {
                    this.showNewReportBadgeSubscription.unsubscribe();
                }

                this.showNewReportBadgeSubscription = timer(3500).subscribe(() => {
                    this.showReportJustCreatedBadge = false;
                });
            });
        }
    }

    public validateCanSearchForPermits(): boolean {
        const hasCrop = this.availableCrops.filter(item => item.IsChecked).length > 0;
        const hasYear = this.availableYears.filter(item => item.IsChecked).length > 0;
        const hasCountry = this.selectedCountryList.length > 0;
        return hasCrop && hasYear && hasCountry;
    }

    public validateCanRunReport(): boolean {
        return (this.selectedRowCount > 0) && this.reportItems.filter(item => item.IsChecked).length > 0;
    }

    public searchForPermits(): void {
        this.isSearching = true;
        this.totalRowCount = 0;
        this.gridOptions.api.setRowData([]);

        DataGrid.ShowLoading(this.gridOptions, 'Searching...');

        this.permitService.Search({
            Crops: this.availableCrops.filter(item => item.IsChecked).map(item => item.Value),
            Years: this.availableYears.filter(item => item.IsChecked).map(item => item.Value),
            Countries: this.selectedCountryList.map(item => item.id)
        } as IPermitSearch).subscribe((results: IPermit[]) => {
            this.hasSearched = true;
            this.isSearching = false;
            this.totalRowCount = results.length;
            this.gridOptions.api.setRowData(results);

            if (this.totalRowCount === 0) {
                DataGrid.ShowLoading(this.gridOptions, 'No Permits Found', true);
            }
            else {
                DataGrid.HideLoading(this.gridOptions);
            }
        });
    }

    public CheckBoxClicked(e): void {
        if (this.availableCrops != null) {
            this.availableCrops.forEach(p => p.IsChecked = true);
        }
    }

    public open(closeAll = false) {
        this.onOpen.emit(false);
        this.modal.open();

        this.totalRowCount = 0;
        this.hasSearched = false;
        this.setupButtons();
    }

    onDestroy() {
        this.clearButtons();
        this.gridOptions.api.setRowData([]);
    }

    canCancel(): boolean {
        return true;
    }
}
